import { identityApiRef, useApi } from "@backstage/core-plugin-api";
import {
	PLATFORM_ADMIN_GROUP_REF,
	type EntitlementGate,
} from "@internal/backstage-plugin-avd-common";
import { useEffect, useState } from "react";

// Who the signed-in colleague IS, as far as the frontend is allowed to care.
//
// UX HIDING ONLY, never the security boundary — the same contract the AVD entitlement gate states at
// length in ../avd/avdEntitlement.tsx. Everything here decides what to RENDER; every real gate is
// server-side (the backend permission policy, and the group check on `GET /bundles`).
//
// Shared by the AVD surfaces and the sidebar's deployed-commit item — which has nothing to do with
// AVD — because both need the same "is this a platform admin?" answer. One implementation, so the
// surfaces cannot drift apart in their failure behaviour, which is the part that matters: they all
// fail CLOSED.

/** Whether the signed-in identity belongs to a set of groups, and whether we know yet. */
export type Membership = { member: boolean; loading: boolean };

/**
 * Reads the signed-in identity's `ownershipEntityRefs` (their own ref plus every group they belong
 * to) and reports whether any of `refs` is among them.
 *
 * Pass `refs` as a module-level constant, never a fresh array literal: it is an effect dependency,
 * so a new array per render would re-run the identity read on every render.
 */
export function useOwnershipIncludes(refs: readonly string[]): Membership {
	const identityApi = useApi(identityApiRef);
	const [state, setState] = useState<Membership>({ member: false, loading: true });

	useEffect(() => {
		let active = true;
		identityApi
			.getBackstageIdentity()
			.then(({ ownershipEntityRefs }) => {
				if (!active) return;
				setState({
					member: ownershipEntityRefs.some((ref) => refs.includes(ref)),
					loading: false,
				});
			})
			.catch(() => {
				// Fail closed (hide) on any identity error — better than flashing a gated surface
				// to somebody who shouldn't see it.
				if (active) setState({ member: false, loading: false });
			});
		return () => {
			active = false;
		};
	}, [identityApi, refs]);

	return state;
}

/** Whether the signed-in identity opens `gate` (one of `ENTITLEMENT_GATES`, whose refs are stable). */
export function useEntitled(gate: EntitlementGate): Membership {
	return useOwnershipIncludes(gate.groupRefs);
}

/**
 * The group that unlocks PLATFORM ADMIN surfaces: `Backstage Admins` ALONE — deliberately narrower
 * than any gate's refs, which also contain that gate's *users* group. Every admin opens every gate;
 * almost nobody a gate opens for is an admin.
 */
const PLATFORM_ADMIN_GROUP_REFS: readonly string[] = [PLATFORM_ADMIN_GROUP_REF];

/** Whether the signed-in identity is a PLATFORM ADMIN, and whether we know yet. */
export type PlatformAdmin = { admin: boolean; loading: boolean };

/**
 * Reads the signed-in identity's group memberships to decide whether the platform team's own
 * surfaces should show: the admin page, and the developer-facing bits of the shell (the
 * deployed-commit item, the link to a provisioning run) that mean nothing to a colleague who cannot
 * open them anyway.
 */
export function usePlatformAdmin(): PlatformAdmin {
	const { member, loading } = useOwnershipIncludes(PLATFORM_ADMIN_GROUP_REFS);
	return { admin: member, loading };
}
